import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';

declare const SignaturePad: any;

@Component({
	selector: 'modal-edit-dependency-outcome',
	templateUrl: './modal-edit-dependency-outcome.component.html',
	styleUrls: ['./modal-edit-dependency-outcome.component.scss']
})
export class ModalEditDependencyOutcomeComponent extends UIModalBaseComponent implements AfterViewInit {

	@ViewChild('spCanvas') spCanvas: ElementRef;

	id;
	list = [];
	disabled = false;
	details;
	mainImage: any;
	currentIndex = 0;
	selectedIndex = 0;
	ratio = 1;
	signaturePad: any;

	constructor(
		public app: AppService,
		private api: ApiService,
	) {
		super();
	}

	get signatureEmpty() {
		if (!this.signaturePad) return true;
		return !!this.signaturePad.isEmpty();
	}

	open(mdata: any) {
		this.id = mdata.id || null;
		this.currentIndex = 0;
		this.selectedIndex = 0;
		this.disabled = false;
		this.refresh();
	}

	refresh() {
		this.api.task.getTaskDependencyOutcome(this.id, (data) => {
			this.list = data;
			this.list.forEach(details => {
				if (details.image_data && details.image_data.image_id && Array.isArray(details.image_data.image_id)) {
					details.image_data = details.image_data.image_id.map((image_id, index) => ({
						image_id,
						image_url: details.image_urls && details.image_urls[index] ? details.image_urls[index] : ''
					}));
				} else {
					details.image_data = [];
				}
			});
			this.details = this.list[this.selectedIndex];
			setTimeout(() => {
				if (this.details.type === 'signature' && !this.details.image_id) {
					this.resizeCanvas();
				}
				if (!this.modal.isOpen) this.modal.open();
			}, 100);
		}, error => {
			this.app.notifications.showDanger(error.message);
			return;
		});
	}

	ngAfterViewInit(): void {
		super.ngAfterViewInit();

		// Prevent closing the modal by clicking on the backdrop. It caused an issue with signature taking,
		// as if a long stroke accidentally ended outside the bounds of the modal, it cancelled the form.
		this.modal.easyClose = false;

		if (this.details && this.details.type === 'signature' && !this.details.image_id) {
			this.signaturePad = new SignaturePad(this.spCanvas.nativeElement, {
				minWidth: 0.5,
				maxWidth: 2
			});
			this.resizeCanvas();
		}
	}

	resizeCanvas() {
		const canvas = this.spCanvas.nativeElement;

		this.ratio = 1;
		canvas.width = canvas.offsetWidth * this.ratio;
		canvas.height = canvas.offsetHeight * this.ratio;
		canvas.getContext('2d').scale(this.ratio, this.ratio);

		this.signaturePad = new SignaturePad(this.spCanvas.nativeElement, {
			minWidth: 0.5,
			maxWidth: 2
		});
		this.signaturePad.clear();
	}

	dependencyChange(event) {
		this.currentIndex = 0;
		this.details = this.list[event];
		setTimeout(() => {
			if (this.details.type === 'signature' && !this.details.image_id) {
				this.resizeCanvas();
			}
		}, 100);
	}

	save() {
		this.disabled = true;
		if (this.details.type === 'signature') {
			if (this.details.image_id) {
				this.disabled = false;
				this.modal.close(this.details.id);
			} else if (this.signatureEmpty) {
				this.app.notifications.showDanger('Signature is empty');
				this.disabled = false;
				return;
			}
			const svgData = typeof this.signaturePad.toSVG === 'function' ? this.signaturePad.toSVG() : this.signaturePad._toSVG();
			const base64Data = svgData.split(',')[1];

			// Decode the base64 string to binary data
			const byteCharacters = atob(base64Data);

			const file = new File([byteCharacters], 'signature.svg', { type: 'image/svg+xml' });
			const formData = new FormData();
			formData.append('userfile', file, 'signature.svg'); // Attach as an SVG file

			// Upload the SVG signature
			this.api.content.uploadUserContent(formData, 'dependency', (data) => {
				if (data.list?.length) {
					this.details.image_url = data.list[0].url;
					this.details.image_id = data.list[0].id;
					this.storeDependency();
				}
			}, (error) => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		} else {
			if (this.details.type === 'photo') {
				// Logic to handle multiple images
				if (this.details.image_data.length > 0) {
					const imageIds = this.details.image_data.map(image => image.image_id);
					this.details.image_data = JSON.stringify({ image_id: imageIds });
					this.details.image_id = imageIds[0];
				} else {
					this.details.image_data = null;
					this.details.image_id = null;
				}
			}

			this.storeDependency();
		}
	}

	storeDependency() {
		this.api.task.storeTaskDependency({ dependency: [this.details] }, result => {
			this.disabled = false;
			this.app.notifications.showSuccess('Snag updated.');
			this.modal.close(result);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	removeImage(currentIndex) {
		if (currentIndex >= 0 && currentIndex < this.details.image_data.length) {
			this.details.image_data.splice(currentIndex, 1);
			this.currentIndex = 0;
		} else {
			this.app.notifications.showDanger('Invalid image index');
		}
	}

	openMediaLibrary(currentIndex) {
		const sub = this.app.onMediaLibrarySelect.subscribe(data => {
			sub.unsubscribe();
			if (data) {
				if (currentIndex >= 0) {
					if (currentIndex < this.details.image_data.length) {
						this.details.image_data[currentIndex].image_id = data.id;
						this.details.image_data[currentIndex].image_url = data.url;
					} else {
						this.details.image_data.push({ image_id: data.id, image_url: data.url });
					}
					this.currentIndex = currentIndex;
				}
			}
		});

		this.app.mediaLibrary.open({ type: 'dependency' });
	}

	setMainImage(i) {
		this.currentIndex = i;
	}

	addMoreImages() {
		this.openMediaLibrary(this.details.image_data.length);
	}

}
